import { formatDuration } from '../../utils/formatDuration';
import { COLORS } from '../../constants/theme';

/** SF-122: one active spell effect line, used in the Active Effects card.
 *  SF-181: `urgent`/`beneficial` are caller-computed — this component renders, it doesn't derive.
 */
interface EffectRowProps {
  name: string;
  magnitude?: string;
  ticksRemaining: number;
  indefinite?: boolean;
  /** Caller computes as `ticksRemaining <= 1 && !indefinite` — the `!indefinite` guard is
   *  load-bearing: an indefinite effect must never be flagged urgent regardless of its
   *  (irrelevant) `ticksRemaining` field. */
  urgent?: boolean;
  /** Owner-perspective impact — see `utils/effectOwnerImpact.ts`. */
  beneficial?: boolean;
}

export function EffectRow({ name, magnitude, ticksRemaining, indefinite, urgent, beneficial }: EffectRowProps) {
  // Day-only mode (no opts) — mirrors formatEffectDuration's non-indefinite branch.
  const duration = indefinite ? 'active' : formatDuration(ticksRemaining);

  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-sm text-game-200 flex items-center gap-1.5">
        {beneficial !== undefined && (
          <span
            aria-hidden="true"
            style={{
              display: 'inline-block',
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: beneficial ? COLORS.positive : COLORS.negative,
              flexShrink: 0,
            }}
          />
        )}
        {name}
        {magnitude && <span className="text-xs ml-1.5 text-game-400">{magnitude}</span>}
      </span>
      <span
        className="text-xs font-mono text-game-400"
        style={urgent ? { color: COLORS.warning } : undefined}
      >
        {/* Non-color urgency cue (WCAG 1.4.1) alongside the color accent above. */}
        {duration}{urgent ? ' · expiring' : ''}
      </span>
    </div>
  );
}
